// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
// import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Valtech Custom Code */
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { useAnalytics } from "@store/Framework/Store/Context/ContextHooks";
import { css } from "styled-components";
/* Valtech Custom Code */

/* Bunzl Custom Code - Start */
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import useHasBeenAddedToList from "@store/Framework/Common/Hooks/Pages/Header/useHasBeenAddedToList";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import HeartFill from "@toolkit/Library/Components/Icons/Heart/HeartFill";
import HeartLine from "@toolkit/Library/Components/Icons/Heart/HeartLine";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
/* Bunzl Custom Code - End */

/* Bunzl Custom Code - Start typed to take unitofmeasure and productid overrides as always string or always undefined*/
type OwnProps = {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
    showAsLink?: boolean;
    hideIcon?: boolean;
    disabled?: boolean;
} & (
    | {
          productIdOverride: string;
          unitOfMeasureOverride: string;
      }
    | {
          productIdOverride?: undefined;
          unitOfMeasureOverride?: undefined;
      }
);

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;
/* Bunzl Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    // Bunzl Custom Code
    currentCurency: state.context.session.currency?.currencyCode,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface ProductAddToListLinkStyles {
    link?: LinkPresentationProps;
    // Bunzl Custom Code
    button?: ButtonPresentationProps;
}

export const productAddToListLinkStyles: ProductAddToListLinkStyles = {
    // Bunzl Custom Code - Start
    link: {
        mergeCss: true,
        css: css`
            /* Bunzl Custom Code */
            transition: all 0.25s ease-in-out;
            :hover {
                ${StyledIcon} {
                    /* Bunzl Custom Code */
                    color: ${getColor("text.linkHover")};
                }
            }
        `,

        icon: {
            position: "left",
            iconProps: {
                src: HeartLine,
                size: 36,
                color: "primary",

                css: css`
                    background-color: ${getColor("primary.contrast")};
                    margin-right: 0;
                    border-radius: 50%;
                    padding: ${pxToRem(8)};
                `,
            },
        },
    },
    // Bunzl Custom Code - End
    button: {
        variant: "tertiary",
        typographyProps: {
            css: css`
                white-space: nowrap;
            `,
        },
        css: css`
            ${({ theme }) => theme?.button?.tertiary?.css}
        `,
    },
};

/* Bunzl Custom Code - Start */
const getLinkAddedStyles = (
    added: boolean,
    linkProps: LinkPresentationProps,
    hideIcon: boolean,
): LinkPresentationProps => ({
    ...linkProps,
    icon: hideIcon
        ? undefined
        : {
              ...linkProps.icon,
              iconProps: {
                  ...linkProps.icon?.iconProps,
                  src: added ? HeartFill : HeartLine,
              },
          },
});
/* Bunzl Custom Code - End */

export const productAddToListLink = productAddToListLinkStyles;

const ProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    // Bunzl Custom Code - Start
    showAsLink,
    hideIcon = true,
    productContext,
    currentCurency,
    productIdOverride,
    unitOfMeasureOverride,
    disabled = false,
    // Bunzl Custom Code - End
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productAddToListLink, extendedStyles));

    // Bunzl Custom Code - Start
    const { hasBeenAddedToList, addProductToList } = useHasBeenAddedToList();
    if (!product?.canAddToWishlist && !productIdOverride) {
        return null;
    }
    const productHasBeenAddedToList = hasBeenAddedToList?.includes(productIdOverride ?? product?.id ?? "");
    const linkStyles = getLinkAddedStyles(productHasBeenAddedToList, styles.link ?? {}, hideIcon);
    const { dispatchAddToListEvent } = useAnalytics();

    const infoToUse: ProductInfo = productIdOverride
        ? {
              qtyOrdered: 1,
              productId: productIdOverride,
              unitOfMeasure: unitOfMeasureOverride,
              productDetailPath: "",
          }
        : productInfo;

    const addToListLinkClickHandler = () => {
        if (!infoToUse) {
            return;
        }
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [infoToUse],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                    addProductToList(productIdOverride ?? product?.id ?? "");
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }
        /* Bunzl Custom Code - End */

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [infoToUse] });
        if (productInfo) {
            dispatchAddToListEvent(productContext, currentCurency); // Bunzl Custom Code
        }
    };

    return (
        /* Bunzl Custom Code - Start */
        <>
            {showAsLink ? (
                <Link
                    // Bunzl Custom Code
                    title={translate("Add to List")}
                    {...linkStyles}
                    onClick={addToListLinkClickHandler}
                    // Bunzl Custom Code
                    disabled={disabled ?? infoToUse.qtyOrdered <= 0}
                    aria-label={translate("Open Add to list modal")}
                >
                    {labelOverride ?? translate("Add to List")}
                </Link>
            ) : (
                <Button
                    // Bunzl Custom Code
                    title={translate("Add to List")}
                    {...styles.button}
                    onClick={addToListLinkClickHandler}
                    // Bunzl Custom Code
                    disabled={disabled ?? infoToUse.qtyOrdered <= 0}
                    aria-label={translate("Open Add to list modal")}
                >
                    {labelOverride ?? translate("Add to List")}
                </Button>
            )}
        </>
        /* Bunzl Custom Code - End */
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAddToListLink));
